import React, { useState, useEffect } from 'react';
import axios from 'axios';
import ShowCard from '../components/ShowCard';
import VideoPlayer from '../components/VideoPlayer';
import { Search, Filter, Tv } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const categories = ['All', 'New Episode', 'Special', 'Drama', 'Documentary', 'Investigation', 'Exclusive'];

// Fallback mock data
const fallbackShows = [
  { id: '1', title: "Caught Red Handed: Season 3 Premiere", thumbnail: "https://images.unsplash.com/photo-1485846234645-a62644f84728?w=600&h=400&fit=crop", youtubeId: "dQw4w9WgXcQ", category: "New Episode", views: "1.2M", duration: "45:00" },
  { id: '2', title: "The Ultimate Betrayal - Special Episode", thumbnail: "https://images.unsplash.com/photo-1536440136628-849c177e76a1?w=600&h=400&fit=crop", youtubeId: "dQw4w9WgXcQ", category: "Special", views: "890K", duration: "52:00" },
  { id: '3', title: "Double Life Exposed: Part 1", thumbnail: "https://images.unsplash.com/photo-1478720568477-152d9b164e26?w=600&h=400&fit=crop", youtubeId: "dQw4w9WgXcQ", category: "Drama", views: "2.1M", duration: "38:00" },
  { id: '4', title: "When Trust Is Broken", thumbnail: "https://images.unsplash.com/photo-1440404653325-ab127d49abc1?w=600&h=400&fit=crop", youtubeId: "dQw4w9WgXcQ", category: "Documentary", views: "567K", duration: "41:00" },
  { id: '5', title: "Secret Affairs: The Investigation", thumbnail: "https://images.unsplash.com/photo-1489599849927-2ee91cede3ba?w=600&h=400&fit=crop", youtubeId: "dQw4w9WgXcQ", category: "Investigation", views: "1.5M", duration: "48:00" },
  { id: '6', title: "The Truth Comes Out", thumbnail: "https://images.unsplash.com/photo-1524712245354-2c4e5e7121c0?w=600&h=400&fit=crop", youtubeId: "dQw4w9WgXcQ", category: "Exclusive", views: "3.2M", duration: "55:00" }
];

const ShowsPage = () => {
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [activeCategory, setActiveCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [shows, setShows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchShows();
  }, []);

  const fetchShows = async () => {
    try {
      const res = await axios.get(`${API}/shows`);
      if (res.data && res.data.length > 0) {
        setShows(res.data);
      } else {
        setShows(fallbackShows);
      }
    } catch (error) {
      console.error('Error fetching shows:', error);
      setShows(fallbackShows);
    }
    setLoading(false);
  };

  const filteredShows = shows.filter(show => {
    const matchesCategory = activeCategory === 'All' || show.category === activeCategory;
    const matchesSearch = show.title.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  return (
    <div className="min-h-screen bg-gray-50 pt-24">
      {/* Header */}
      <section className="bg-gradient-to-r from-gray-900 to-gray-800 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-4 mb-4">
            <Tv size={40} className="text-red-500" />
            <h1 className="text-4xl md:text-5xl font-black text-white">
              All <span className="text-red-500">Shows</span>
            </h1>
          </div>
          <p className="text-gray-400 text-lg max-w-2xl">
            Watch all episodes, specials, and exclusive content. REAL. RAW. UNFILTERED.
          </p>
        </div>
      </section>

      {/* Filters */}
      <section className="bg-white border-b border-gray-200 sticky top-20 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            {/* Search */}
            <div className="relative w-full md:w-80">
              <Search size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search shows..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none transition-all"
              />
            </div>

            {/* Categories */}
            <div className="flex items-center gap-2 overflow-x-auto pb-2 md:pb-0 w-full md:w-auto">
              <Filter size={18} className="text-gray-400 flex-shrink-0" />
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setActiveCategory(category)}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                    activeCategory === category
                      ? 'bg-red-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Shows Grid */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="text-center py-20">
              <div className="animate-spin w-10 h-10 border-4 border-red-600 border-t-transparent rounded-full mx-auto"></div>
              <p className="text-gray-500 mt-4">Loading shows...</p>
            </div>
          ) : filteredShows.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredShows.map((show) => (
                <ShowCard key={show.id} show={show} onPlay={setSelectedVideo} />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <Tv size={60} className="text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-600">No shows found</h3>
              <p className="text-gray-400 mt-2">Try adjusting your search or filter</p>
            </div>
          )}
        </div>
      </section>

      {/* Video Player Modal */}
      {selectedVideo && (
        <VideoPlayer
          youtubeId={selectedVideo.youtubeId}
          title={selectedVideo.title}
          onClose={() => setSelectedVideo(null)}
        />
      )}
    </div>
  );
};

export default ShowsPage;
